/**
 * Translates canonical/internal domain vocabulary at the presentation boundary.
 *
 * API routes, event names, database records, and TypeScript identifiers intentionally keep the
 * platform kernel's stable keys. Product copy uses the consumer vocabulary from the design
 * handoff, including when an internal enum or audit event is rendered dynamically.
 */
const PRODUCT_REPLACEMENTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bevidence contracts\b/giu, "alert rules"],
  [/\bevidence contract\b/giu, "alert rule"],
  [/\belaboration\b/giu, "analysis"],
  [/\bimmutable\b/giu, "saved"],
  [/\bprovenance\b/giu, "source details"],
  [/\btripwires\b/giu, "alert rules"],
  [/\btripwire\b/giu, "alert rule"],
  [/\bdecision artifacts\b/giu, "reports"],
  [/\bdecision artifact\b/giu, "report"],
  [/\bdecision packs\b/giu, "reports"],
  [/\bdecision pack\b/giu, "report"],
  [/\bpacks\b/giu, "reports"],
  [/\bpack\b/giu, "report"],
  [/\bresearch cases\b/giu, "case studies"],
  [/\bresearch case\b/giu, "case study"],
  [/\bwhat-if lab\b/giu, "quick test"],
  [/\binvestigating\b/giu, "researching"],
  [/\binvestigations\b/giu, "research"],
  [/\binvestigation\b/giu, "research"],
  [/\binvestigate\b/giu, "research"],
  [/\bscenarios\b/giu, "what-ifs"],
  [/\bscenario\b/giu, "what-if"],
  [/\bbooks\b/giu, "portfolios"],
  [/\bbook\b/giu, "portfolio"],
];

const DATA_COMMAND_LABELS: Readonly<Record<string, string>> = {
  "openbb.equity.search": "Find a stock",
  "openbb.equity.price.quote": "Latest share price",
  "openbb.equity.price.historical": "Share price history",
  "openbb.equity.profile": "Company overview",
  "openbb.equity.fundamental.metrics": "Company financial measures",
  "openbb.etf.holdings": "Investments inside a fund",
  "openbb.etf.equity_exposure": "Companies inside a fund",
  "openbb.etf.sectors": "Industries inside a fund",
  "openbb.etf.countries": "Countries inside a fund",
  "openbb.news.company": "Company news",
  "openbb.fixedincome.government.treasury_rates": "US Treasury interest rates",
  "openbb.economy.fred_series": "Economic time series",
  "openbb.derivatives.options.chains": "Available options contracts",
  "openbb.regulators.sec.institutions_search": "Find an SEC-registered institution",
};

/** What the product calls its financial data service; the provider underneath is OpenBB. */
export const FINANCIAL_DATA_NAME = "Polyfuture Financial Data";

/**
 * A provider name or catalog text as the interface shows it: OpenBB is presented as Polyfuture
 * Financial Data. Only the wording on screen changes; stored source details keep their names.
 */
export function financialDataWording(text: string): string {
  return text
    .replace(/\b([Aa])n OpenBB\b/g, `$1 ${FINANCIAL_DATA_NAME}`)
    .replace(/\bOpenBB(?:-powered|-backed)?\b/g, FINANCIAL_DATA_NAME);
}

export function dataCommandLabel(commandId: string): string {
  return (
    DATA_COMMAND_LABELS[commandId] ??
    commandId
      .replace(/^openbb\./, "")
      .replaceAll(/[._]/g, " ")
      .replace(/^./, (letter) => letter.toUpperCase())
  );
}

/** Converts an internal enum/event key into user-facing product language. */
export function productLabel(value: string): string {
  return replaceProductVocabulary(value.replaceAll("_", " ").replaceAll(".", " "));
}

/** Rewrites controlled system copy without changing user-authored or source-authored records. */
export function replaceProductVocabulary(value: string): string {
  return PRODUCT_REPLACEMENTS.reduce(
    (current, [pattern, replacement]) =>
      current.replace(pattern, (matched) => preserveCase(matched, replacement)),
    value,
  );
}

const CONTROLLED_VALUE_KEYS = new Set([
  "action",
  "entity",
  "eventtype",
  "field",
  "kind",
  "purpose",
  "recordtype",
  "scope",
  "status",
  "targettype",
  "triggertype",
  "type",
  "view",
]);

/** Builds a readable reproduction view while keeping user/source-authored values verbatim. */
export function productRecordView(value: unknown, fieldKey?: string): unknown {
  if (typeof value === "string") {
    if (fieldKey === "key" && CONTROLLED_SECTION_KEYS.has(value)) return productLabel(value);
    return fieldKey !== undefined && CONTROLLED_VALUE_KEYS.has(normalizeKey(fieldKey))
      ? productLabel(value)
      : value;
  }
  if (Array.isArray(value)) {
    return value.map((nested) => productRecordView(nested, fieldKey));
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
        productKeyLabel(key),
        productRecordView(nested, key),
      ]),
    );
  }
  return value;
}

/** Adds report-specific controlled prose while preserving customer-authored research and actions. */
export function productReportManifestView(value: unknown): unknown {
  const view = productRecordView(value);
  if (!isRecord(value) || !isRecord(view)) return view;
  const limitations = Array.isArray(value["limitations"])
    ? value["limitations"].map((item) =>
        typeof item === "string" ? replaceProductVocabulary(item) : productRecordView(item),
      )
    : view["limitations"];
  const sections = Array.isArray(value["sections"])
    ? value["sections"].map((section) => {
        const sectionView = productRecordView(section);
        if (!isRecord(section) || !isRecord(sectionView)) return sectionView;
        return {
          ...sectionView,
          ...(typeof section["title"] === "string"
            ? { title: replaceProductVocabulary(section["title"]) }
            : {}),
          ...(typeof section["narrative"] === "string"
            ? { narrative: replaceProductVocabulary(section["narrative"]) }
            : {}),
        };
      })
    : view["sections"];
  const research =
    isRecord(value["investigation"]) && isRecord(view["research"])
      ? {
          ...view["research"],
          ...(typeof value["investigation"]["title"] === "string"
            ? { title: replaceProductVocabulary(value["investigation"]["title"]) }
            : {}),
        }
      : view["research"];
  return { ...view, limitations, sections, ...(research === undefined ? {} : { research }) };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

const CONTROLLED_SECTION_KEYS = new Set([
  "executive_summary",
  "trigger_scope",
  "evidence_timeline",
  "scenario_analysis",
  "decisions_actions",
  "limitations_verification",
]);

function productKeyLabel(value: string): string {
  return productLabel(value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase());
}

function normalizeKey(value: string): string {
  return value.replaceAll(/[_.-]+/g, "").toLowerCase();
}

function preserveCase(source: string, replacement: string): string {
  if (source === source.toUpperCase()) return replacement.toUpperCase();
  if (source[0] === source[0]?.toUpperCase()) {
    return replacement[0]?.toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/**
 * Renders a stored position size for display.
 *
 * `size_text` is written into immutable book-version snapshots, so it is never rewritten. It is
 * also inconsistent: the same column holds `USD 12000000000`, a bare `700` for a position that
 * does carry a currency, and `not supplied`. Format at the presentation boundary instead, and
 * prefer the structured `sizeValue`/`currency` the payload already carries, because they state the
 * amount and its currency unambiguously. Both derive from a money notional, never a share count.
 */
export function formatExposureSize(input: {
  sizeText: string;
  sizeValue?: number | string | null;
  currency?: string | null;
}): string {
  const currency = normalizeCurrencyCode(input.currency);
  const amount = toFiniteAmount(input.sizeValue);
  if (amount !== undefined) return formatAmount(amount, currency);

  const text = input.sizeText.trim();
  if (text.length === 0 || /^not supplied$/iu.test(text)) return "Not supplied";
  const grouped = groupLongDigitRuns(text);
  if (currency === undefined || grouped.toUpperCase().includes(currency)) return grouped;
  return `${currency} ${grouped}`;
}

const DECIMAL_SIZE_FORMAT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

function formatAmount(amount: number, currency: string | undefined): string {
  if (currency === undefined) return DECIMAL_SIZE_FORMAT.format(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unrecognized ISO code is still worth showing next to a grouped amount.
    return `${currency} ${DECIMAL_SIZE_FORMAT.format(amount)}`;
  }
}

function normalizeCurrencyCode(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : undefined;
}

function toFiniteAmount(value: number | string | null | undefined): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value !== "string" || value.trim().length === 0) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Groups runs of four or more digits, leaving magnitude shorthand such as `12.5m` and identifiers
 * such as `US0000000001` alone — the latter by refusing to group digits that follow a letter.
 */
function groupLongDigitRuns(text: string): string {
  return text.replaceAll(/(?<![\p{L}\d.,])\d{4,}/gu, (digits) =>
    digits.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ","),
  );
}
